import { Link } from 'react-router';
import { ApiError, type components } from '@awj/api-client';
import { formatNumber } from '@awj/i18n';
import { Skeleton, useToast } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useConfirm } from '~/lib/confirm.tsx';
import { useCategories, useDeleteCategory } from '~/lib/catalog-queries.ts';
import { categoryColumns } from '~/lib/export-columns.ts';
import { useRowLink } from '~/lib/row-link.ts';

type Category = components['schemas']['AdminCategoryResponse'];

/**
 * The category tree.
 *
 * Flat rows with an indent rather than a collapsible tree: the shop has two levels and a dozen
 * categories, and a tree control would be more machinery than the thing it organises.
 *
 * Editing happens on {@link CategoryEditorPage}, at a URL of its own. The form used to be a
 * dialog over this list; a category carries two names, two slugs, two descriptions and a
 * placement, which is a page's worth of decisions rather than a sheet's.
 */
export function CategoriesPage() {
  const { t, locale } = useI18n();
  const toast = useToast();

  const categories = useCategories();
  const remove = useDeleteCategory();
  const confirm = useConfirm();
  const rowLink = useRowLink();

  const all = categories.data ?? [];
  const roots = all.filter((category) => category.parentId == null);
  const childrenOf = (id: number) => all.filter((category) => category.parentId === id);
  // The order the table reads in, which the export keeps: each parent followed by its children.
  const tree = roots.flatMap((root) => [root, ...childrenOf(root.id)]);

  function row(category: Category, depth: number) {
    return (
      <tr key={category.id} {...rowLink(`/categories/${category.id}`)}>
        <td className="data-table__td">
          <div className={depth > 0 ? 'category-cell category-cell--child' : 'category-cell'}>
            <Link className="data-table__link category-name" to={`/categories/${category.id}`}>
              <bdi>{locale === 'ar' ? category.nameAr : category.nameEn}</bdi>
            </Link>
            <span className="data-table__sub">
              <bdi>{locale === 'ar' ? category.slugAr : category.slugEn}</bdi>
            </span>
          </div>
        </td>
        <td className="data-table__td">
          <span className={`status status--${category.isActive ? 'active' : 'archived'}`}>
            {category.isActive ? t('catalog.active') : t('catalog.hidden')}
          </span>
        </td>
        <td className="data-table__td data-table__cell--end">
          <bdi>{formatNumber(locale, category.productCount)}</bdi>
        </td>
        <td className="data-table__td data-table__cell--end">
          {/*
            Icon buttons rather than two text buttons per row: a table of a dozen categories
            carried twenty-four word-shaped controls down its edge, which is more visual weight
            than the data. Each still carries its own name for anyone not reading the glyph.
          */}
          <div className="row-actions">
            <Link
              className="icon-button"
              to={`/categories/${category.id}`}
              aria-label={t('common.edit')}
              title={t('common.edit')}
            >
              <Icon name="edit" />
            </Link>
            <button
              type="button"
              className="icon-button icon-button--danger"
              aria-label={t('common.delete')}
              title={t('common.delete')}
              onClick={async () => {
                const name = locale === 'ar' ? category.nameAr : category.nameEn;

                if (!(await confirm.ask({ title: t('confirm.deleteNamed', { name }), body: t('confirm.cannotUndo') }))) {
                  return;
                }

                try {
                  await remove.mutateAsync(category.id);
                  toast.show({ title: t('catalog.deleted'), tone: 'success' });
                } catch (caught) {
                  // The API refuses a category that still holds products, and explains. That is
                  // the useful message, not a generic failure.
                  toast.show({
                    title: t('catalog.couldNotDelete'),
                    message: caught instanceof ApiError ? caught.problem.detail : undefined,
                    tone: 'danger',
                  });
                }
              }}
            >
              <Icon name="trash" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <PageTitle
        title={t('nav.categories')}
        actions={
          <>
            <ExportMenu
              file="categories"
              sheet={t('nav.categories')}
              columns={categoryColumns(t, locale, all)}
              rows={() => tree}
              disabled={tree.length === 0}
            />
            <Link className="awj-button awj-button--primary awj-button--sm" to="/categories/new">
              <Icon name="plus" size={17} />
              {t('catalog.newCategory')}
            </Link>
          </>
        }
      />

      {categories.isPending ? (
        <Skeleton height="260px" />
      ) : all.length === 0 ? (
        <div className="empty-panel">
          <p>{t('catalog.noCategories')}</p>
        </div>
      ) : (
        <div className="data-table">
          <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('nav.categories')}>
            <table className="data-table__table">
              <thead>
                <tr>
                  <th className="data-table__th" scope="col">{t('catalog.category')}</th>
                  <th className="data-table__th" scope="col">{t('catalog.status')}</th>
                  <th scope="col" className="data-table__th data-table__cell--end">
                    {t('catalog.productCount')}
                  </th>
                  <th scope="col" className="data-table__th data-table__cell--end data-table__tight">
                    <span className="sr-only">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tree.map((category) => row(category, category.parentId == null ? 0 : 1))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirm.dialog}
    </>
  );
}
